/**
 * Task 204 — React hook over the reading record (see reading-record.ts).
 *
 * Holds the current reading point in state and persists every change to
 * sessionStorage synchronously (immediate + unambiguous, like the chat's
 * clearSession). `save` replaces the current point and returns the new bookmark
 * so the caller can fire the landing animation against it.
 */
import { useCallback, useState } from "react";
import {
  EMPTY_RECORD,
  READING_RECORD_SCHEMA_VERSION,
  activeBookmark,
  capBookmarks,
  clampSnippet,
  loadRecord,
  saveRecord,
  type ReadingBookmark,
  type ReadingRecord,
} from "./reading-record";

function newBookmarkId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface SaveBookmarkInput {
  book: string;
  chapter: number;
  anchor?: string | null;
  snippet: string;
  scrollY?: number;
}

export interface ReadingRecordApi {
  /** The current reading point, or null. */
  active: ReadingBookmark | null;
  /** Save (replace) the current reading point; returns the stored bookmark. */
  save: (input: SaveBookmarkInput) => ReadingBookmark;
  /** Remove the current reading point. */
  clear: () => void;
}

export function useReadingRecord(): ReadingRecordApi {
  const [record, setRecord] = useState<ReadingRecord>(loadRecord);

  const save = useCallback((input: SaveBookmarkInput): ReadingBookmark => {
    const bookmark: ReadingBookmark = {
      id: newBookmarkId(),
      book: input.book,
      chapter: input.chapter,
      snippet: clampSnippet(input.snippet),
      createdAt: new Date().toISOString(),
    };
    if (typeof input.anchor === "string" && input.anchor.length > 0) {
      bookmark.anchor = input.anchor;
    }
    if (typeof input.scrollY === "number" && Number.isFinite(input.scrollY) && input.scrollY >= 0) {
      bookmark.scrollY = input.scrollY;
    }
    setRecord((prev) => {
      const next: ReadingRecord = {
        schemaVersion: READING_RECORD_SCHEMA_VERSION,
        // Newest first so capBookmarks keeps the latest reading point.
        bookmarks: capBookmarks([bookmark, ...prev.bookmarks]),
        activeBookmarkId: bookmark.id,
      };
      saveRecord(next);
      return next;
    });
    return bookmark;
  }, []);

  const clear = useCallback((): void => {
    setRecord(() => {
      saveRecord(EMPTY_RECORD);
      return EMPTY_RECORD;
    });
  }, []);

  return { active: activeBookmark(record), save, clear };
}
